import React from "react";
import { Users, CalendarDays, CreditCard } from "lucide-react";
import { useNavigate } from "react-router-dom";

const InHouseGuestReport = ({ inHouseGuest }) => {
  const navigate = useNavigate();

  const transformedGuests = inHouseGuest.map((guestData) => {
    const guest = guestData.guest || {};

    const checkIn = new Date(guestData.check_in_date_time);
    const checkOut = new Date(guestData.check_out_date_time);
    const nights = (checkOut - checkIn) / (1000 * 60 * 60 * 24); // Rough estimate
    const totalAmount = guestData?.folios?.length ? guestData?.folios[0].charges?.reduce((acc, curr) => acc + ((curr.charge_type==="room_charge"|| curr.charge_type==="service_charge") ? parseFloat(curr.amount)*(parseFloat(curr.tax_rate)+100)/100 : parseFloat(curr.amount)), 0) : 0;
    const paidAmount = guestData?.folios?.length ? guestData?.folios[0].charges?.filter(charge => charge.payment_status===true)?.reduce((acc, curr) => acc + ((curr.charge_type==="room_charge"|| curr.charge_type==="service_charge") ? parseFloat(curr.amount)*(parseFloat(curr.tax_rate)+100)/100 : parseFloat(curr.amount)), 0) : 0;
    const balance = totalAmount - paidAmount;
    return {
      id: guestData.id,
      guestName: `${guest.first_name || ""} ${guest.last_name || ""}`.trim(),
      roomNumber: guestData.room_name || "N/A", // or use room number if available
      checkInDate: guestData.check_in_date_time.split(" ")[0],
      checkOutDate: guestData.check_out_date_time.split(" ")[0],
      adults: 1 + guestData?.additional_guests?.length, // If adults/children not available in data, set defaults or fetch them from related data
      children: 0,
      roomType: guestData.room_type || "Standard",
      bookingId: guestData.booking_id,
      booking_status: guestData.booking_status,
      totalAmount: totalAmount,
      paidAmount: paidAmount, // Update this if payment data is available
      balance: balance, // same as total if unpaid
      folioNumber: `FL-${guestData.booking_id?.slice(-3) || "000"}`,
      nights,
      rate: parseFloat((guestData.total_amount / nights).toFixed(2)) || 0,
    };
  });

  const getBalanceColor = (balance) => {
    if (balance === 0) return "text-success";
    if (balance > 0) return "text-danger";
    return "text-secondary";
  };

  const calculateNightsStayed = (checkInDate, currentDate) => {
    const checkIn = new Date(checkInDate);
    const current = new Date(currentDate);
    const diffTime = Math.abs(current - checkIn);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  return (
    <div className="card mb-4">
      <div className="card-header d-flex align-items-center gap-2">
        <Users className="text-success" size={20} />
        <h5 className="mb-0">In-House Guests</h5>
      </div>
      <div className="card-body">
        <p className="text-muted">
          Currently checked-in guests as of{" "}
          {/* {new Date(selectedDate).toLocaleDateString()}  */}
        </p>

        {transformedGuests?.map((guest) => (
          <div key={guest.id} className="border rounded-3 p-4 mb-4">
            <div className="d-flex justify-content-between gap-3 mb-3">
              <div>
                <h6 className="mb-1">{guest.guestName}</h6>
                <div className="d-flex gap-2 justify-content-center">
                  <small className="text-muted">
                    Booking ID: {guest.bookingId}
                  </small>
                  <span className="badge bg-secondary mb-1">
                    Room {guest.roomNumber}
                  </span>
                  <span className="badge bg-primary mb-1">{guest.roomType}</span>
                </div>
              </div>
              {guest.booking_status === "check_in" && (
                <button
                  className="btn btn-outline-danger mb-2"
                  onClick={() =>
                    navigate(
                      `/reservation/check-out-booking/${guest.id}`
                    )
                  }
                >
                  <i className="bi bi-box-arrow-left me-1"></i>
                  Check Out
                </button>
              )}
            </div>

            <div className="row mb-3">
              <div className="col-md-3">
                <div className="text-muted d-flex align-items-center mb-1">
                  <CalendarDays size={16} className="me-2" />
                  <span>Check-in</span>
                </div>
                <div>{new Date(guest.checkInDate).toLocaleDateString()}</div>
                <small className="text-muted">
                  {/* {calculateNightsStayed(guest.checkInDate, selectedDate)}{" "} */}
                  {/* nights stayed */}
                </small>
              </div>
              <div className="col-md-3">
                <div className="text-muted d-flex align-items-center mb-1">
                  <CalendarDays size={16} className="me-2" />
                  <span>Check-out</span>
                </div>
                <div>{new Date(guest.checkOutDate).toLocaleDateString()}</div>
                <small className="text-muted">
                  {Math.ceil(guest.nights)} total nights
                </small>
              </div>
              <div className="col-md-3">
                <div className="text-muted d-flex align-items-center mb-1">
                  <Users size={16} className="me-2" />
                  <span>Occupancy</span>
                </div>
                <div>{guest.adults} Adults</div>
                {guest.children > 0 && (
                  <small className="text-muted">
                    {guest.children} Children
                  </small>
                )}
              </div>
              <div className="col-md-3">
                <div className="text-muted d-flex align-items-center mb-1">
                  <CreditCard size={16} className="me-2" />
                  <span>Financial</span>
                </div>
                <div>Total: ₹{guest.totalAmount}</div>
                <small className="text-muted">Paid: ₹{guest.paidAmount}</small>
                <div
                  className={`fw-semibold ${getBalanceColor(guest.balance)}`}
                >
                  Balance: ₹{guest.balance}
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-between border-top pt-3 mt-3">
              <div className="text-muted small">
                Folio: {guest.folioNumber} &nbsp; | &nbsp; Rate: ₹{guest.rate}
                /night
              </div>
              {/* <div className="d-flex gap-2">
                <button className="btn btn-outline-secondary btn-sm">
                  View Folio
                </button>
                <button className="btn btn-outline-secondary btn-sm">
                  Add Charges
                </button>
                {guest.balance > 0 && (
                  <button className="btn btn-primary btn-sm">
                    Process Payment
                  </button>
                )}
              </div> */}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default InHouseGuestReport;
